import { useState } from 'react';
import { buildReport, money } from '../lib/report';
import { downloadAssessmentReport } from '../lib/assessmentReport';
import { bandMessages, disclaimer, reportConfig, strongerSetup } from '../config/report';

export default function ReportScreen({ answers, contact, delivery, assessment }) {
  const report = buildReport(answers);
  const [percent, setPercent] = useState(50);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const next = report.grade === 'A'
    ? {title:'Want to see what this could look like for your business?',body:'Book a free 20-minute process review. I will walk through your results with you and show you a real system built for a business like yours.'}
    : report.grade === 'B'
      ? {title:'Your next step',body:'Download your report and review the risks above. If you would like to talk it through, reply to your report email or contact us.'}
      : {title:'Keep this for later',body:'Your report is available to keep. If your situation changes, we are happy to help.'};

  const download = async () => {
    setDownloading(true);setDownloadError('');
    try {
      await downloadAssessmentReport(assessment,answers,contact);
      window.dispatchEvent(new CustomEvent('bpa:report-download'));
    } catch (error) {
      console.error('PDF generation failed',error);
      setDownloadError('Unable to create your PDF. Please try again.');
    } finally { setDownloading(false); }
  };

  return (
    <section className="report-screen expanded premium-report" aria-label="Your health report">
      <header className="report-hero">
        <div className="report-hero-top">
          <span className="question-kicker">YOUR PERSONALISED HEALTH REPORT</span>
          <button type="button" className="report-download-button" disabled={downloading} onClick={download}>
            {downloading ? 'Preparing PDF…' : '↓ Download PDF report'}
          </button>
        </div>
        <h1>Here's your business health report, {contact.firstName}.</h1>
        <p className="report-business">Prepared for <strong>{contact.businessName}</strong></p>
      </header>

      <div className="report-overview">
        <div className="report-score-block">
          <div className="report-score-label">OVERALL HEALTH SCORE</div>
          <div className="report-score-value">{report.score}<span>/100</span></div>
          <div className="report-score-meter"><span style={{width:report.score+'%'}}/></div>
        </div>
        <div className="report-score-details">
          <span className={'report-status status-'+report.band.toLowerCase().replace(' ','-')}>{report.band}</span>
          <h2>{report.band === 'At risk' ? 'Your process needs attention' : report.band === 'Strained' ? 'There is room to streamline' : 'Your process is in good shape'}</h2>
          <p>{bandMessages[report.band]}</p>
        </div>
      </div>

      <div className="report-panel">
        <div className="report-section-head"><span className="report-section-icon">01</span><div><h2>Your four operational areas</h2><p>Each area contributes up to 25 points to your overall score.</p></div></div>
        <div className="report-areas-grid">
          {report.areas.map(area=><div className="report-area-card" key={area.key}>
            <div className="report-area-title"><strong>{area.label}</strong><span>{area.score}<small>/25</small></span></div>
            <div className="report-area-track"><span style={{width:(area.score/25*100)+'%'}}/></div>
            <p>{area.short}</p>
          </div>)}
        </div>
      </div>

      <div className="report-panel">
        <div className="report-section-head"><span className="report-section-icon">02</span><div><h2>Your top three risks</h2><p>Prioritised by your lowest-scoring areas.</p></div></div>
        <div className="report-risks">
          {report.topRisks.map((area,i)=><div className="report-risk-card" key={area.key}>
            <span className="report-risk-number">{String(i+1).padStart(2,'0')}</span>
            <div><h3>{area.label}</h3><p>{area.long}</p></div>
          </div>)}
        </div>
      </div>

      <div className="report-panel">
        <div className="report-section-head"><span className="report-section-icon">03</span><div><h2>The cost of manual administration</h2><p>A financial illustration based on the figures you provided.</p></div></div>
        <div className="report-cost-grid">
          <div className="report-cost-primary"><span>ESTIMATED ANNUAL TIME COST</span><strong>{money(report.annualCost)}</strong><small>{answers.q8} hours/week × {money(answers.q9)}/hour × 52 weeks</small></div>
          <div className="report-cost-recovery"><span>ILLUSTRATIVE TIME VALUE RECOVERED</span><strong>{money(report.annualCost*percent/100)}</strong><label htmlFor="recover">If you won back <b>{percent}%</b> of this time</label><input id="recover" type="range" min="10" max="90" step="5" value={percent} onChange={e=>setPercent(Number(e.target.value))}/></div>
        </div>
        <p className="report-estimate-note">Illustrative estimate based on your own answers. Not a guarantee of savings.</p>
      </div>

      <div className="report-panel">
        <div className="report-section-head"><span className="report-section-icon">04</span><div><h2>What a stronger setup looks like</h2><p>Ways a tailored timesheet-to-invoice system can improve the process.</p></div></div>
        <div className="report-check-grid">{strongerSetup.map(item=><div className="report-check" key={item}><span aria-hidden="true">✓</span><p>{item}</p></div>)}</div>
      </div>

      <div className="report-next-step">
        <span className="question-kicker">RECOMMENDED NEXT STEP</span>
        <h2>{next.title}</h2><p>{next.body}</p>
        {report.grade==='A' && <><p className="report-case-study">{reportConfig.caseStudy}</p>{reportConfig.bookingUrl ? <a className="report-download-button" href={reportConfig.bookingUrl} target="_blank" rel="noopener noreferrer" onClick={()=>window.dispatchEvent(new CustomEvent('bpa:booking-click'))}>Book my free process review</a> : <p className="report-secondary-note">Booking link will be available shortly.</p>}</>}
        {report.grade==='B' && <a className="report-outline-button" href={'mailto:'+reportConfig.contactEmail}>Contact BPA Solutions</a>}
      </div>
      <div className="report-bottom-action"><div><strong>Keep a copy of your results</strong><p>Download your personalised, branded A4 report.</p></div><button type="button" className="report-download-button" disabled={downloading} onClick={download}>{downloading?'Preparing PDF…':'↓ Download my PDF'}</button></div>
      {downloadError && <p role="alert" className="form-error">{downloadError}</p>}
      {delivery && <p className="delivery-message" role="status">{delivery}</p>}
      <p className="report-disclaimer">{disclaimer}</p>
    </section>
  );
}